import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Custom Confirmation Dialog Component
 * 
 * Replaces native window.confirm() with a branded, accessible dialog
 * 
 * Usage:
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete Challenge?"
 *   description="This action cannot be undone. The challenge will be permanently deleted."
 *   confirmText="Delete"
 *   cancelText="Cancel"
 *   onConfirm={handleDelete}
 *   variant="destructive"
 * />
 */
const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "default", // "default" | "destructive"
}) => {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  // Color variants for confirm button
  const confirmButtonClass = variant === "destructive"
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-yellow-500 hover:bg-yellow-600 text-slate-900";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 border-2 border-yellow-500/30">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white text-xl font-bold">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-300 text-base">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={confirmButtonClass}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
